import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  fetchProviderAccountSnapshot,
  getProviderBalanceValue,
} from '../utils/providerBalances';
import {
  ArrowLeft,
  Truck,
  Edit2,
  RefreshCcw,
  Landmark,
  Mail,
  Phone,
  MapPin,
  FileText,
  CreditCard,
  Calendar,
  DollarSign,
  X,
  ExternalLink,
  Trash2,
  AlertTriangle,
  Check,
  Search
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchProviderLists, fetchProviderPrices, renombrarLista } from '../utils/providerPriceLists';

/**
 * Detalle de Proveedor - Layout estándar (Estilo Productos)
 * - Muestra TODOS los campos conocidos del proveedor en tarjetas
 * - Movimientos: combina compras, pagos y ajustes manuales del proveedor.
 * - Permite ANULAR (Soft Delete) retrotrayendo Stock, Cta Cte y Caja.
 * - Confirmación integrada "Sí, borrar" / "No".
 */

const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });
const normalizeText = (value) => String(value || '').trim().toLowerCase();
const isPaymentLikeEntry = (movement) =>
  normalizeText(movement?.tipo_comprobante || movement?.type || movement?.tipo) === 'pago';
const isAnnulmentLikeEntry = (movement) =>
  normalizeText(movement?.type || movement?.tipo || movement?.estado).includes('anul');

export default function ProveedoresDetalle({ onNavigate }) {
  const { id } = useParams();
  const { profile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [procesandoAnulacion, setProcesandoAnulacion] = useState(false);
  const [confirmingAnulacion, setConfirmingAnulacion] = useState(false); 
  
  const [proveedor, setProveedor] = useState(null);
  const [movimientosCuenta, setMovimientosCuenta] = useState([]); 
  const [selectedMov, setSelectedMov] = useState(null);
  
  // NUEVO: Estado para búsqueda
  const [q, setQ] = useState('');

  const [uiMsg, setUiMsg] = useState(null); 
  const showMsg = (type, title, message) => setUiMsg({ type, title, message });

  useEffect(() => {
    if (profile?.organization_id && id) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, id]);

  // Listas de precio del proveedor. Se cargan aparte de cargar() a proposito:
  // si el modulo no esta habilitado o la tabla todavia no existe en esta base,
  // el detalle del proveedor tiene que seguir funcionando igual.
  const [listasPrecio, setListasPrecio] = useState([]);
  const [renombrando, setRenombrando] = useState(null); // { id, nombre }
  const [guardandoNombre, setGuardandoNombre] = useState(false);

  const guardarNombreLista = async () => {
    if (!renombrando) return;
    const limpio = String(renombrando.nombre || '').trim();
    if (!limpio) { toast.error('El nombre de la lista es obligatorio'); return; }
    setGuardandoNombre(true);
    try {
      const actualizada = await renombrarLista(profile.organization_id, renombrando.id, limpio);
      setListasPrecio((actuales) => actuales.map((l) => (l.id === actualizada.id ? { ...l, ...actualizada } : l)));
      setRenombrando(null);
      toast.success('Lista renombrada');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'No se pudo renombrar la lista');
    } finally {
      setGuardandoNombre(false);
    }
  };
  const [listaAbierta, setListaAbierta] = useState(null);
  const [preciosLista, setPreciosLista] = useState([]);
  const [cargandoPrecios, setCargandoPrecios] = useState(false);

  useEffect(() => {
    if (!profile?.organization_id || !id) return;
    fetchProviderLists(profile.organization_id, { proveedorId: id })
      .then(setListasPrecio)
      .catch(() => setListasPrecio([]));
  }, [profile?.organization_id, id]);

  const abrirLista = async (lista) => {
    setListaAbierta(lista);
    setCargandoPrecios(true);
    try {
      const precios = await fetchProviderPrices(profile.organization_id, {
        listaId: lista.id,
        soloVigentes: false,
      });

      // Se resuelven los nombres acá y no con un join: la lista puede tener
      // cientos de lineas y el detalle se abre poco.
      const ids = [...new Set(precios.map((precio) => precio.product_id))];
      let nombres = {};
      if (ids.length > 0) {
        const { data } = await supabase
          .from('products')
          .select('id, name, barcode')
          .in('id', ids);
        nombres = Object.fromEntries((data || []).map((prod) => [prod.id, prod]));
      }

      setPreciosLista(
        precios
          .map((precio) => ({
            ...precio,
            producto: nombres[precio.product_id]?.name || 'Producto eliminado',
            barcode: nombres[precio.product_id]?.barcode || precio.codigo_proveedor || '',
          }))
          .sort((a, b) => String(a.producto).localeCompare(String(b.producto), 'es'))
      );
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron cargar los precios de la lista');
      setPreciosLista([]);
    } finally {
      setCargandoPrecios(false);
    }
  };

  const cargar = async () => {
    setLoading(true);
    setSelectedMov(null);
    setConfirmingAnulacion(false);
    try {
      const orgId = profile.organization_id;

      const { data: p, error: pErr } = await supabase
        .from('proveedores')
        .select('*')
        .eq('organization_id', orgId)
        .eq('id', id)
        .single();
      if (pErr) throw pErr;
      setProveedor(p || null);

      const { data: providerData, error: providerError, timeline } = await fetchProviderAccountSnapshot({
        providerId: id,
        organizationId: orgId,
        supabaseClient: supabase,
      });
      if (providerError) throw providerError;
      setProveedor(providerData || p || null);
      setMovimientosCuenta(timeline || []);

      setUiMsg(null);
    } catch (e) {
      console.error(e);
      showMsg('error', 'No se pudo cargar el proveedor', e?.message || 'Error inesperado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedMov) return;

    const stillExists = (movimientosCuenta || []).some(
      (movement) => movement.timelineKey === selectedMov.timelineKey,
    );

    if (stillExists) return;
    setSelectedMov(null);
    setConfirmingAnulacion(false);
  }, [movimientosCuenta, selectedMov]);

  const executeAnulacion = async () => {
    if (!selectedMov) return;
    if (selectedMov.entryKind !== 'purchase') {
      toast.error('Solo se pueden anular compras o pagos registrados.');
      return;
    }
    
    setProcesandoAnulacion(true);
    const toastId = toast.loading("Procesando anulación y reversión...");

    try {
        const orgId = profile.organization_id;
        const movementRecordId =
          selectedMov.compra_id ||
          String(selectedMov.id || '')
            .replace(/^purchase:/, '')
            .replace(/^account_movement:/, '');
        const monto = Math.abs(Number(selectedMov.total_general || selectedMov.amount || 0));
        const esPago = isPaymentLikeEntry(selectedMov);
        const saldoActualProveedor = Number(proveedor?.saldo || 0);
        const saldoProveedorRestituido = esPago
          ? saldoActualProveedor + monto
          : saldoActualProveedor - monto;
        const anulacionFecha = new Date().toISOString();
        
        if (esPago) {
            // === LÓGICA ANULACIÓN DE PAGO (RESTITUCIÓN DE DEUDA Y DINERO) ===
            
            await supabase
              .from('proveedores')
              .update({ saldo: saldoProveedorRestituido })
              .eq('id', id);

            // 2. Revertir Banco 
            // NUEVO: Intentamos buscar primero por documento_id (el nuevo estándar de TesoreriaPagos)
            let matchFin = null;
            const { data: exactMatch } = await supabase
                .from('movimientos_financieros')
                .select('*')
                .eq('organization_id', orgId)
                .eq('documento_id', movementRecordId)
                .eq('is_active', true)
                .maybeSingle();

            if (exactMatch) {
                matchFin = exactMatch;
            } else {
                // LÓGICA LEGACY: Búsqueda heurística por tiempo
                const timeBuffer = 2 * 60 * 1000; 
                const movTime = new Date(selectedMov.created_at).getTime();
                const minTime = new Date(movTime - timeBuffer).toISOString();
                const maxTime = new Date(movTime + timeBuffer).toISOString();

                const { data: finMovs } = await supabase
                    .from('movimientos_financieros')
                    .select('*')
                    .eq('organization_id', orgId)
                    .eq('entidad_id', id)
                    .eq('tipo', 'Egreso') 
                    .eq('is_active', true)
                    .gte('created_at', minTime)
                    .lte('created_at', maxTime);

                matchFin = finMovs?.find(m => Math.abs(Number(m.monto) - monto) < 0.1);
            }

            if (matchFin && matchFin.bank_id) {
                const { data: bank } = await supabase.from('banks').select('current_balance').eq('id', matchFin.bank_id).single();
                if (bank) {
                    const nuevoSaldoBanco = Number(bank.current_balance) + monto;
                    await supabase.from('banks').update({ current_balance: nuevoSaldoBanco }).eq('id', matchFin.bank_id);
                    
                    await supabase.from('movimientos_financieros').update({ 
                        descripcion: `(ANULADO) ${matchFin.descripcion}`,
                        monto: 0, 
                        is_active: false
                    }).eq('id', matchFin.id);
                    
                    toast.success("Saldo bancario restaurado.", { id: toastId });
                }
            } else {
                toast("Saldo proveedor corregido. No se encontró mov. bancario automático.", { icon: '⚠️', id: toastId });
            }

        } else {
            // === LÓGICA ANULACIÓN DE COMPRA (QUITA DE DEUDA Y STOCK) ===
            
            await supabase
              .from('proveedores')
              .update({ saldo: saldoProveedorRestituido })
              .eq('id', id);

            // 2. Revertir Stock 
            if (selectedMov.sitio_id && Array.isArray(selectedMov.items) && selectedMov.items.length > 0) {
                for (const item of selectedMov.items) {
                    const prodId = item.product_id || item.id;
                    const qty = Number(item.quantity || item.cantidad || 0);

                    if (prodId && qty > 0) {
                        const { data: stockLevel } = await supabase
                            .from('stock_levels')
                            .select('quantity')
                            .eq('product_id', prodId)
                            .eq('site_id', selectedMov.sitio_id)
                            .single();
                        
                        if (stockLevel) {
                            const nuevoStock = Number(stockLevel.quantity) - qty;
                            await supabase
                                .from('stock_levels')
                                .update({ quantity: nuevoStock })
                                .eq('product_id', prodId)
                                .eq('site_id', selectedMov.sitio_id);
                        }
                    }
                }
                toast.success("Stock descontado correctamente.", { id: toastId });
            }
        }

        const { error: providerMovementError } = await supabase
          .from('proveedor_movimientos')
          .insert([
            {
              organization_id: orgId,
              proveedor_id: id,
              compra_id: movementRecordId,
              tipo: esPago ? 'Anulacion de Pago' : 'Anulacion de Compra',
              monto: esPago ? monto : -monto,
              saldo_resultante: saldoProveedorRestituido,
              fecha: anulacionFecha,
            },
          ]);
        if (providerMovementError) throw providerMovementError;

        // 3. Marcar COMPRA como ANULADA (Soft Delete)
        const { error: purchaseUpdateError } = await supabase
            .from('compras')
            .update({ 
                estado: 'ANULADO', 
                total_general: 0, 
                observaciones: `(ANULADO el ${new Date().toLocaleDateString()}) ${selectedMov.observaciones || ''}` 
            })
            .eq('id', movementRecordId);
        if (purchaseUpdateError) throw purchaseUpdateError;

        await cargar(); 
        toast.success("Movimiento anulado correctamente", { id: toastId });

    } catch (e) {
        console.error(e);
        toast.error("Error al anular: " + e.message, { id: toastId });
    } finally {
        setProcesandoAnulacion(false);
        setConfirmingAnulacion(false);
    }
  };

  const kpis = useMemo(() => {
    const validos = movimientosCuenta || [];
    const totalCompras = validos
      .filter((mov) => mov.entryKind === 'purchase' && !isPaymentLikeEntry(mov) && !isAnnulmentLikeEntry(mov))
      .reduce((acc, mov) => acc + Number(mov.amount || 0), 0);
    const totalPagos = validos
      .filter((mov) => isPaymentLikeEntry(mov) && !isAnnulmentLikeEntry(mov))
      .reduce((acc, mov) => acc + Math.abs(Number(mov.amount || 0)), 0);
    const saldo = Number(
      movimientosCuenta?.[0]?.current_balance ?? getProviderBalanceValue(proveedor),
    );
    return {
      totalCompras,
      totalPagos,
      saldo
    };
  }, [movimientosCuenta, proveedor]);

  // Filtrado de movimientos
  const movimientosFiltrados = useMemo(() => {
      if (!q.trim()) return movimientosCuenta;
      const needle = q.toLowerCase();
      return movimientosCuenta.filter((mov) => 
          (mov.numero_comprobante || '').toLowerCase().includes(needle) ||
          (mov.type || mov.tipo || mov.tipo_comprobante || '').toLowerCase().includes(needle) ||
          (mov.observations || mov.observaciones || '').toLowerCase().includes(needle) ||
          (mov.estado || '').toLowerCase().includes(needle)
      );
  }, [movimientosCuenta, q]);

  const goBack = () => {
    if (onNavigate) return onNavigate('proveedores');
    window.history.back();
  };

  const onRegistrarPago = () => {
    try {
      if (onNavigate) return onNavigate('tesoreria-pagos', { proveedorId: id });
      window.location.href = `/sistema/tesoreria-pagos`;
    } catch (e) {
      console.error(e);
    }
  };

  const onAjustarCuentaCorriente = () => {
    try {
      if (onNavigate) return onNavigate('proveedores-movimiento', id);
      window.location.href = `/sistema/proveedores-movimiento/${id}`;
    } catch (e) {
      console.error(e);
    }
  };

  if (loading && !proveedor) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD]" size={40} />
      </div>
    );
  }

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER - Estilo Productos */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="w-10 h-10 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-50 transition-all shrink-0"
            title="Volver"
          >
            <ArrowLeft size={18} className="text-slate-600" />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Truck size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Proveedor</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {proveedor?.razon_social || '—'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => (onNavigate ? onNavigate(`proveedores-editar/${id}`) : null)}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Edit2 size={16} /> Editar
          </button>
          <button
            onClick={onAjustarCuentaCorriente}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <FileText size={16} /> Ajuste Cta Cte
          </button>
          <button
            onClick={onRegistrarPago}
            className="px-5 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <CreditCard size={16} /> Registrar Pago
          </button>
        </div>
      </div>

      {/* Mensajes inline */}
      {uiMsg && (
        <div className={`mb-5 rounded-2xl border p-4 shadow-sm bg-white ${uiMsg.type === 'error' ? 'border-rose-200' : 'border-slate-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">{uiMsg.title}</div>
              <div className="text-[11px] font-bold text-slate-500 mt-1">{uiMsg.message}</div>
            </div>
            <button onClick={() => setUiMsg(null)} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50" title="Cerrar">
              <X size={16} className="text-slate-500" />
            </button>
          </div>
        </div>
      )}

      {/* KPIs - Estilo Productos */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-amber-300 transition-all">
          <div className="flex items-center justify-between relative z-10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Saldo Actual</p>
            <DollarSign size={16} className="text-slate-300 group-hover:text-amber-500 transition-colors" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight relative z-10">$ {fmtMoney(kpis.saldo)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cuenta corriente</p>
          <div className="absolute -right-4 -bottom-4 text-amber-50 opacity-20 group-hover:opacity-40 transition-opacity">
              <DollarSign size={80} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-[#51A7FD]/30 transition-all">
          <div className="flex items-center justify-between relative z-10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Compras</p>
            <FileText size={16} className="text-slate-300 group-hover:text-[#51A7FD] transition-colors" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight relative z-10">$ {fmtMoney(kpis.totalCompras)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Acumulado</p>
          <div className="absolute -right-4 -bottom-4 text-blue-50 opacity-20 group-hover:opacity-40 transition-opacity">
              <FileText size={80} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-emerald-200 transition-all">
          <div className="flex items-center justify-between relative z-10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pagos Realizados</p>
            <CreditCard size={16} className="text-slate-300 group-hover:text-emerald-500 transition-colors" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight relative z-10">$ {fmtMoney(kpis.totalPagos)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Acumulado</p>
          <div className="absolute -right-4 -bottom-4 text-emerald-50 opacity-20 group-hover:opacity-40 transition-opacity">
              <CreditCard size={80} />
          </div>
        </div>
      </div>

      {/* Tarjetas de detalle */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:border-slate-300 transition-colors">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2 mb-4">
            <Landmark size={14} className="text-[#51A7FD]" /> Identificación
          </div>
          <div className="space-y-3">
            <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Razón Social</p>
                <p className="text-[11px] font-black text-slate-800 uppercase">{proveedor?.razon_social || '—'}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">CUIT</p>
                    <p className="text-[10px] font-bold text-slate-600">{proveedor?.cuit || '—'}</p>
                </div>
                <div>
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Condición IVA</p>
                    <p className="text-[10px] font-bold text-slate-600 truncate">{proveedor?.tax_condition || '—'}</p>
                </div>
            </div>
            <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Estado</p>
                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${proveedor?.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                    {proveedor?.is_active ? 'Activo' : 'Inactivo'}
                </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:border-slate-300 transition-colors">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2 mb-4">
            <MapPin size={14} className="text-rose-400" /> Geolocalización y contacto
          </div>
          <div className="space-y-3">
             <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Dirección Comercial</p>
                <p className="text-[10px] font-bold text-slate-600 uppercase truncate">{proveedor?.direccion || '—'}</p>
            </div>
            <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Teléfono</p>
                <p className="text-[10px] font-bold text-slate-600 flex items-center gap-2"><Phone size={12} className="text-slate-400"/> {proveedor?.telefono || '—'}</p>
            </div>
            <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Correo Electrónico</p>
                <p className="text-[10px] font-bold text-slate-600 flex items-center gap-2"><Mail size={12} className="text-slate-400"/> {proveedor?.email || '—'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:border-slate-300 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <FileText size={14} className="text-[#51A7FD]" /> Listas de precio
            </div>
            <div className="flex items-center gap-1.5">
              {listasPrecio.length > 0 && (
                <button
                  onClick={() => onNavigate?.('comparador-precios')}
                  className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-600 text-[9px] font-black uppercase tracking-widest hover:text-[#51A7FD] hover:border-[#51A7FD]/40 transition-all"
                >
                  Comparar
                </button>
              )}
              <button
                onClick={() => onNavigate?.('importar-lista-proveedor', id)}
                className="px-3 py-1.5 rounded-xl bg-[#0B2A36] text-[#3FF2AA] text-[9px] font-black uppercase tracking-widest hover:opacity-90 transition-all"
              >
                Importar lista
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {listasPrecio.length === 0 ? (
              <p className="text-[10px] font-bold text-slate-400">
                Sin listas cargadas. Importar una no modifica los costos de tus productos.
              </p>
            ) : (
              listasPrecio.slice(0, 5).map((lista) => (
                renombrando?.id === lista.id ? (
                  <div key={lista.id} className="flex items-center gap-2 bg-white border border-[#51A7FD]/40 rounded-xl px-3 py-2">
                    <input
                      autoFocus
                      value={renombrando.nombre}
                      onChange={(e) => setRenombrando({ ...renombrando, nombre: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') guardarNombreLista();
                        if (e.key === 'Escape') setRenombrando(null);
                      }}
                      maxLength={120}
                      disabled={guardandoNombre}
                      className="min-w-0 flex-1 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                    />
                    <button type="button" onClick={guardarNombreLista} disabled={guardandoNombre} title="Guardar" className="p-1.5 rounded-lg bg-[#0B2A36] text-[#3FF2AA] disabled:opacity-50">
                      <Check size={12} />
                    </button>
                    <button type="button" onClick={() => setRenombrando(null)} disabled={guardandoNombre} title="Cancelar" className="p-1.5 rounded-lg border border-slate-200 text-slate-500">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                <div key={lista.id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => abrirLista(lista)}
                  className="min-w-0 flex-1 text-left flex items-center justify-between gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 hover:border-[#51A7FD]/40 hover:bg-white transition-all"
                >
                  <div className="min-w-0">
                    <p className="text-[10px] font-black text-slate-700 uppercase truncate">
                      {lista.nombre}
                    </p>
                    <p className="text-[9px] font-bold text-slate-400">
                      Desde {lista.vigencia_desde} · {lista.moneda} · ver precios
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border ${
                      lista.anulada_at
                        ? 'bg-slate-100 text-slate-500 border-slate-200'
                        : lista.vigencia_hasta
                        ? 'bg-amber-50 text-amber-700 border-amber-100'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                    }`}
                  >
                    {lista.anulada_at ? 'anulada' : lista.vigencia_hasta ? 'cerrada' : 'vigente'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setRenombrando({ id: lista.id, nombre: lista.nombre || '' })}
                  title="Renombrar lista"
                  className="shrink-0 p-2 rounded-xl border border-slate-100 bg-slate-50 text-slate-400 hover:text-[#51A7FD] hover:border-[#51A7FD]/40 transition-all"
                >
                  <Edit2 size={12} />
                </button>
                </div>
                )
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:border-slate-300 transition-colors">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2 mb-4">
            <CreditCard size={14} className="text-emerald-500" /> Datos Bancarios
          </div>
          <div className="space-y-3">
             <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Entidad Bancaria</p>
                <p className="text-[10px] font-bold text-slate-600 uppercase">{proveedor?.bank_name || '—'}</p>
            </div>
            <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">CBU / Alias</p>
                <p className="text-[10px] font-bold text-slate-600 truncate">{proveedor?.cbu_alias || '—'}</p>
            </div>
            <div>
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Cond. Pago Default</p>
                <p className="text-[10px] font-bold text-slate-600 uppercase">{proveedor?.default_payment_condition || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* FILTROS Y BÚSQUEDA - Estilo Productos */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4 flex items-center gap-3">
         <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
            <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="BUSCAR MOVIMIENTO, COMPROBANTE..."
                className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase transition-all"
            />
          </div>
      </div>

      {/* TABLA DE MOVIMIENTOS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-10">
        <div className="bg-[#0B2A36] px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">Cuenta Corriente ({movimientosFiltrados.length})</span>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full text-left min-w-[700px]">
            <thead className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-400">
              <tr>
                <th className="px-6 py-4">Fecha</th>
                <th className="px-4 py-4">Movimiento</th>
                <th className="px-4 py-4">Detalle</th>
                <th className="px-4 py-4 text-right">Saldo</th>
                <th className="px-4 py-4 text-right">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {movimientosFiltrados.length === 0 ? (
                <tr><td colSpan={5} className="py-20 text-center text-[10px] font-black uppercase text-slate-300 italic">Sin movimientos encontrados</td></tr>
              ) : (
                movimientosFiltrados.map((c) => {
                  const movementDate = c.created_at || c.fecha;
                  const amount = Number(c.amount || c.monto || 0);
                  const isPago = isPaymentLikeEntry(c);
                  const isAnulado = normalizeText(c.estado) === 'anulado';
                  const movementLabel = c.type || c.tipo || c.tipo_comprobante || (c.entryKind === 'purchase' ? 'Compra' : 'Movimiento');
                  const movementDetail = c.numero_comprobante || c.observations || c.observaciones || c.condicion_pago || 'Sin detalle';
                  const isExpanded = selectedMov?.timelineKey === c.timelineKey;
                  const displayBalance = Number(c.current_balance ?? c.saldo_resultante ?? 0);
                  const realMovementId = c.compra_id || c.id;
                  c.fecha = movementDate;
                  c.tipo_comprobante = movementLabel;
                  c.numero_comprobante = movementDetail;
                  c.estado = isAnulado ? 'ANULADO' : `$ ${fmtMoney(displayBalance)}`;
                  c.total_general = Math.abs(amount);
                  c.observaciones = c.observations || c.observaciones;
                  return (
                    <React.Fragment key={c.timelineKey || realMovementId}>
                        <tr
                          className={`hover:bg-blue-50/20 transition-all cursor-pointer group ${isAnulado ? 'bg-rose-50/30 opacity-70' : ''}`}
                          onClick={() => { 
                              setSelectedMov(isExpanded ? null : { ...c, id: realMovementId });
                              setConfirmingAnulacion(false); 
                          }}
                        >
                          <td className="px-6 py-4">
                            <div className={`text-[11px] font-bold ${isAnulado ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{c.fecha ? new Date(c.fecha).toLocaleDateString('es-AR') : '—'}</div>
                            <div className="text-[9px] font-bold text-slate-400">{c.fecha ? new Date(c.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                          </td>
                          <td className="px-4 py-4 text-[10px] font-black uppercase">
                            <span className={`px-2 py-1 rounded-md border text-[9px] font-black uppercase tracking-widest ${isPago ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                              {isPago ? 'PAGO' : (c.tipo_comprobante || (c.entryKind === 'account_movement' ? c.type : null) || 'COMPRA')}
                            </span>
                          </td>
                          <td className={`px-4 py-4 text-[10px] font-bold uppercase ${isAnulado ? 'text-slate-400 line-through' : 'text-slate-600 group-hover:text-[#51A7FD] transition-colors'}`}>{c.numero_comprobante || '-'}</td>
                          <td className="px-4 py-4 text-right text-[10px] font-bold text-slate-600">
                            {isAnulado ? <span className="text-rose-500 font-black">ANULADO</span> : c.estado || '-'}
                          </td>
                          <td className="px-4 py-4 text-right text-[12px] font-black">
                             <span className={`${isAnulado ? 'text-slate-400 line-through' : (amount < 0 ? 'text-emerald-600' : 'text-rose-600')}`}>
                                {amount < 0 ? '-' : '+'}$ {fmtMoney(c.total_general)}
                             </span>
                          </td>
                        </tr>
                        
                        {/* PANEL EXPANDIBLE DE DETALLE E INLINE ACTION */}
                        {isExpanded && (
                            <tr className="bg-slate-50 animate-in fade-in slide-in-from-top-1">
                                <td colSpan="5" className="px-6 py-4 border-b border-slate-100">
                                    <div className="flex flex-col xl:flex-row items-start justify-between gap-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-inner mb-4">
                                        <div>
                                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-2">
                                                <FileText size={16} className="text-[#51A7FD]"/> Detalle Técnico del Movimiento
                                            </div>
                                            <div className="text-[11px] font-bold text-slate-500 mt-1 uppercase">
                                            {movementLabel} • {c.fecha ? new Date(c.fecha).toLocaleString('es-AR') : ''}
                                            </div>
                                            
                                            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-6">
                                                <div>
                                                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Subtotal</p>
                                                    <p className="text-[11px] font-bold text-slate-700">$ {fmtMoney(c.subtotal)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">IVA</p>
                                                    <p className="text-[11px] font-bold text-slate-700">$ {fmtMoney(c.iva)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Cond. Pago</p>
                                                    <p className="text-[11px] font-bold text-slate-700 uppercase">{c.condicion_pago || '—'}</p>
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Observaciones</p>
                                                <p className="text-[10px] font-bold text-slate-600 uppercase max-w-2xl">{c.observaciones || 'Sin observaciones'}</p>
                                            </div>
                                        </div>
                                        
                                        {/* ACCIONES DE ANULACIÓN */}
                                        <div className="flex items-center gap-2 shrink-0 bg-slate-50 p-2 rounded-xl border border-slate-100">
                                            {c.entryKind === 'purchase' && !isAnulado && (
                                                <>
                                                    {confirmingAnulacion ? (
                                                        <div className="flex items-center gap-2 animate-in zoom-in duration-200">
                                                            <span className="text-[10px] font-black text-rose-500 uppercase px-2 tracking-widest">¿Confirmar?</span>
                                                            <button 
                                                                onClick={executeAnulacion}
                                                                disabled={procesandoAnulacion}
                                                                className="flex items-center gap-1 px-3 py-1.5 bg-rose-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-rose-600 transition-colors shadow-sm"
                                                            >
                                                                {procesandoAnulacion ? <RefreshCcw size={12} className="animate-spin"/> : <Check size={12} strokeWidth={3}/>} Sí, borrar
                                                            </button>
                                                            <button 
                                                                onClick={() => setConfirmingAnulacion(false)}
                                                                className="px-3 py-1.5 bg-white border border-slate-200 text-slate-500 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-slate-50 transition-colors shadow-sm"
                                                            >
                                                                No
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button 
                                                            onClick={() => setConfirmingAnulacion(true)} 
                                                            className="px-4 py-2 bg-white text-rose-500 border border-rose-200 hover:bg-rose-50 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm"
                                                            title="Anular movimiento y revertir efectos (Stock, Caja, Deuda)"
                                                        >
                                                            <Trash2 size={14} /> Anular Registro
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* LISTADO DE ITEMS SI LOS HAY */}
                                    {Array.isArray(c.items) && c.items.length > 0 && (
                                        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Items Incluidos</div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                {c.items.map((it, idx) => (
                                                <div key={idx} className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex justify-between items-center">
                                                    <div className="text-[10px] font-black text-slate-800 uppercase truncate pr-2" title={it?.name || it?.producto || it?.producto_nombre || 'Item'}>
                                                        {it?.name || it?.producto || it?.producto_nombre || 'Item'}
                                                    </div>
                                                    <div className="text-[10px] font-bold text-slate-500 shrink-0">
                                                        <span className="bg-white border border-slate-200 px-1.5 py-0.5 rounded mr-2">x{it?.quantity ?? it?.cantidad ?? '—'}</span> 
                                                        $ {fmtMoney(it?.price ?? it?.precio ?? it?.unit_price)}
                                                    </div>
                                                </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    {listaAbierta && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="min-w-0">
                <h2 className="text-[12px] font-black uppercase tracking-widest text-slate-800 truncate">
                  {listaAbierta.nombre}
                </h2>
                <p className="text-[10px] font-bold text-slate-400">
                  Desde {listaAbierta.vigencia_desde}
                  {listaAbierta.vigencia_hasta ? ` hasta ${listaAbierta.vigencia_hasta}` : ' · vigente'}
                  {' · '}{listaAbierta.moneda}
                  {listaAbierta.anulada_at ? ' · anulada' : ''}
                </p>
              </div>
              <button
                onClick={() => { setListaAbierta(null); setPreciosLista([]); }}
                className="text-slate-400 hover:text-slate-700 shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-auto flex-1">
              {cargandoPrecios ? (
                <div className="py-16 flex flex-col items-center gap-2 text-slate-300">
                  <RefreshCcw className="animate-spin" size={28} />
                  <p className="text-[10px] font-black uppercase tracking-widest">Cargando precios...</p>
                </div>
              ) : preciosLista.length === 0 ? (
                <div className="py-16 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                  La lista no tiene precios cargados
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-slate-400 border-b border-slate-100 text-[9px] font-black uppercase tracking-[0.2em]">
                      <th className="px-6 py-3">Producto</th>
                      <th className="px-6 py-3">Código proveedor</th>
                      <th className="px-6 py-3 text-right">Costo</th>
                      <th className="px-6 py-3 text-right">Bulto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {preciosLista.map((precio) => (
                      <tr key={precio.id} className={precio.vigente ? '' : 'opacity-45'}>
                        <td className="px-6 py-2.5">
                          <p className="text-[11px] font-black text-slate-700 truncate">{precio.producto}</p>
                          {!precio.vigente && (
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                              no vigente
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-2.5 text-[10px] font-bold text-slate-500">
                          {precio.codigo_proveedor || '—'}
                        </td>
                        <td className="px-6 py-2.5 text-right text-[11px] font-black text-slate-700">
                          $ {fmtMoney(precio.costo)}
                        </td>
                        <td className="px-6 py-2.5 text-right text-[10px] font-bold text-slate-500">
                          {precio.bulto || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-400">
                {preciosLista.length} producto{preciosLista.length === 1 ? '' : 's'} en la lista
              </span>
              <button
                onClick={() => { setListaAbierta(null); setPreciosLista([]); }}
                className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
